// Phase H+1 Mini-Wave 5 · Nav config V12 USER · 15 gruppi + role-based visibility.
// Phase I Wave 1.22 (F85) · migration · every item now points to `/v2/*`
// canonical paths via `to` (SPA Link). The former `legacyHref` fields
// (which triggered full-page browser reloads and bled auth state) are
// removed from the sidebar config · placeholder pages at `/v2/<slug>`
// use `V12ComingSoonPage` (see `data/v12_coming_soon_registry.js`) and
// expose an explicit "Open classic version" link inside the page itself,
// so operators keep their escape hatch without the sidebar losing SPA
// semantics.
//
// V12 Achromatic Strict · italiano puro (fallback en via i18n).
//
// Role matrix (riferimento PRD V10):
//   - `user`           → cluster 1-14 (admin-area cluster 15 nascosto)
//   - `admin` / `super_admin` → cluster 1-15 (tutto)
//   - `affiliate_only` → Workspace minimale (credits + payout + partnership + account)
//   - `ai_agent`       → redirect `/dashboard` legacy (nessun accesso dati sensibili)
//   - unknown          → redirect `/dashboard` legacy (fail-safe)
//
// Chiavi i18n namespace `user_nav.*` per evitare collisioni con `nav.*` admin.

import { applyUserDiscoveryExtensions } from "./v12_user_nav_discovery_extensions";
import { applyUserCommunityExtensions } from "./v12_user_nav_community_extensions";
import { applyUserAdminShortcutsExtensions } from "./v12_user_nav_admin_shortcuts_extensions";
import { applyUserScaleFeaturesExtensions } from "./v12_user_nav_scale_features_extensions";

export const V12_USER_NAV_GROUPS = [
    {
        id: "workspace",
        label: "Workspace",
        icon: "LayoutDashboard",
        allowFor: ["user", "admin", "super_admin", "affiliate_only"],
        items: [
            { id: "workspace-dashboard",     label: "Dashboard",       to: "/app/dashboard",            implemented: true, icon: "Gauge",    keywords: ["home", "overview", "panoramica"] },
            { id: "workspace-activity",      label: "Attività recenti", to: "/app/dashboard#activity", implemented: true, icon: "Activity", keywords: ["feed", "attività", "recent"] },
            { id: "workspace-notifications", label: "Notifiche",       to: "/app/notifications",        implemented: true, icon: "Bell",     keywords: ["notifiche", "alerts"] },
            // Phase IX.F.4 · A1 · Moduli attivi (PRD V10 gap closure)
            { id: "workspace-modules",       label: "Moduli attivi",   to: "/app/modules",              implemented: true, icon: "Boxes",    keywords: ["moduli", "plan", "piano", "feature"] },
            // Phase IX.F.4 · A2 · Statistiche personali (PRD V10 gap closure)
            { id: "workspace-stats",         label: "Le mie statistiche", to: "/app/stats",             implemented: true, icon: "LineChart", keywords: ["stats", "kpi", "analytics", "statistiche"] },
        ],
    },
    {
        id: "lead-outreach",
        label: "Lead & Outreach",
        icon: "Target",
        allowFor: ["user", "admin", "super_admin"],
        items: [
            { id: "lo-lead-engine",         label: "Lead Engine",         to: "/app/leads",                     implemented: true, icon: "Target",   keywords: ["lead", "prospect"] },
            { id: "lo-saved-leads",         label: "Lead salvati",        to: "/app/leads/saved",               implemented: true, icon: "Bookmark", keywords: ["salvati", "bookmark"] },
            { id: "lo-outreach-templates",  label: "Template outreach",   to: "/app/outreach/templates",        implemented: true, icon: "FileText", keywords: ["template", "modello"] },
            { id: "lo-outreach-sequences",  label: "Sequenze",            to: "/app/outreach/sequences",        implemented: true, icon: "Layers",   keywords: ["sequenza", "cadenza"] },
            { id: "lo-outreach-campaigns",  label: "Campagne",            to: "/app/outreach/campaigns",        implemented: true, icon: "Mail",     keywords: ["campagna"] },
            { id: "lo-outreach-home",       label: "Outreach",            to: "/app/outreach",                  implemented: true, icon: "Mail",     keywords: ["outreach", "home"] },
            { id: "lo-m10-leads",           label: "Leads · M10",         to: "/app/leads/m10",                 implemented: true, icon: "Users",    keywords: ["m10", "leads"], hidden: true /* Phase VI.5 Wave A · HIDE · future_phase_hint=phase_viii_m10_leads */ },
            { id: "lo-m10-outreach",        label: "Outreach Seq · M10",  to: "/app/outreach/m10-sequences",    implemented: true, icon: "Mail",     keywords: ["m10", "outreach"], hidden: true /* Phase VI.5 Wave A · HIDE · future_phase_hint=phase_viii_m10_outreach */ },
            // Phase I Wave 3.1 / 3.2 · Social + content discovery (growth+).
            { id: "lo-lead-discovery",      label: "Lead Discovery",      to: "/app/leads/discovery",           implemented: true, icon: "Search",   keywords: ["serper", "reddit", "linkedin", "discovery"] },
            { id: "lo-content-discovery",   label: "Content Discovery",   to: "/app/content/discovery",         implemented: true, icon: "Youtube",  keywords: ["youtube", "medium", "substack", "content"] },
            // Phase I Wave 3.3 · B2C Social Discovery (growth+ · TikTok tab scale+).
            { id: "lo-social-discovery",    label: "Social Discovery (B2C)", to: "/app/social/discovery",       implemented: true, icon: "Users",    keywords: ["twitter", "facebook", "instagram", "tiktok", "quora", "b2c"] },
            // Phase I Wave 5 · Lead Enrichment (Apollo + Hunter + NeverBounce · MOCK lazy).
            { id: "lo-lead-enrichment",     label: "Lead Enrichment",     to: "/app/leads/enrichment",          implemented: true, icon: "Sparkles", keywords: ["apollo", "hunter", "neverbounce", "enrichment", "verify"], hidden: true /* Phase VI.5 Wave CL · HIDE · page renders MockBadge visibly (apollo/hunter MOCK lazy) */ },
        ],
    },
    {
        id: "crm",
        label: "CRM",
        icon: "Users",
        allowFor: ["user", "admin", "super_admin"],
        items: [
            // F86 · CRM cluster MVP · real pages wired to /api/crm/*.
            // Italian path slugs per operator spec: contatti/aziende/deal/pipeline/routing/agenti.
            { id: "crm-contacts", label: "Contatti",         to: "/app/crm/contatti", implemented: true, icon: "User",       keywords: ["contact", "contatti"] },
            { id: "crm-accounts", label: "Account aziendali", to: "/app/crm/aziende",  implemented: true, icon: "Building2",  keywords: ["account", "azienda"] },
            { id: "crm-deals",    label: "Deal",             to: "/app/crm/deal",     implemented: true, icon: "Target",     keywords: ["deal", "opportunità"] },
            { id: "crm-pipeline", label: "Pipeline",         to: "/app/crm/pipeline", implemented: true, icon: "Workflow",   keywords: ["pipeline", "stadi"] },
            { id: "crm-routing",  label: "Routing",          to: "/app/crm/routing",  implemented: true, icon: "Workflow",   keywords: ["routing"], hidden: true /* Phase VI.5 Wave CL · HIDE · static placeholder page · no backend wiring */ },
            { id: "crm-agents",   label: "Agenti",           to: "/app/crm/agenti",   implemented: true, icon: "User",       keywords: ["agent"], hidden: true /* Phase VI.5 Wave CL · HIDE · static placeholder page · no backend wiring */ },
        ],
    },
    {
        id: "ai-suite",
        label: "AI Suite",
        icon: "Sparkles",
        allowFor: ["user", "admin", "super_admin"],
        items: [
            { id: "ai-universe",      label: "AI Universe",   to: "/app/ai/universe",     implemented: true, icon: "Sparkles",    keywords: ["universe", "ai"] },
            // Phase IX.F.4 · A3 · AI Agents entry esplicito (PRD V10 gap closure)
            { id: "ai-agents",        label: "AI Agents",     to: "/app/ai/agents",       implemented: true, icon: "Sparkles",    keywords: ["agents", "agenti", "ai"] },
            // Phase IX.F.8 · AutoSEO Long-form articles
            { id: "ai-seo-longform",  label: "SEO Long-form", to: "/app/ai/seo/long-form", implemented: true, icon: "FileText",    keywords: ["seo", "long-form", "articles", "autoseo"] },
            { id: "ai-rag",           label: "AI RAG",        to: "/app/ai/rag",          implemented: true, icon: "Database",    keywords: ["rag", "vector", "knowledge", "documents"] },
            { id: "ai-civilization",  label: "Civilization",  to: "/app/ai/civilization", implemented: true, icon: "Sparkles",    keywords: ["civiltà"], hidden: true /* Phase VI.5 Wave CL · HIDE · ModuleGate concept · no real collection */ },
            { id: "ai-badges",        label: "Badge",         to: "/app/ai/badges",       implemented: true, icon: "ShieldCheck", keywords: ["badge"] },
            { id: "ai-leaderboard",   label: "Leaderboard",   to: "/app/ai/leaderboard",  implemented: true, icon: "Trophy",      keywords: ["classifica"] },
            { id: "ai-prompts",       label: "Prompt library", to: "/app/ai/prompts",     implemented: true, icon: "ScrollText",  keywords: ["prompt"] },
        ],
    },
    {
        id: "funnel-decisioni",
        label: "Funnel & Decisioni",
        icon: "Workflow",
        allowFor: ["admin", "super_admin"],
        hidden: true, /* Phase VI.5 Wave A · HIDE cluster · future_phase_hint=phase_vii_funnel_decisions */
        items: [
            { id: "fd-ai-decisions", label: "AI Decisions",  to: "/app/funnel/ai-decisions", implemented: true, icon: "Sparkles",    keywords: ["ai", "decisioni"], hidden: true },
            { id: "fd-workflows",    label: "Workflow",      to: "/app/funnel/workflows",    implemented: true, icon: "Workflow",    keywords: ["workflow"], hidden: true },
            { id: "fd-policies",     label: "Policy",        to: "/app/funnel/policies",     implemented: true, icon: "ScrollText",  keywords: ["policy"], hidden: true },
            { id: "fd-approvals",    label: "Approvazioni",  to: "/app/funnel/approvals",    implemented: true, icon: "ShieldCheck", keywords: ["approvazione"], hidden: true },
            { id: "fd-audit",        label: "Audit",         to: "/app/funnel/audit",        implemented: true, icon: "ScrollText",  keywords: ["audit"], hidden: true },
            { id: "fd-supervisor",   label: "Supervisor",    to: "/app/funnel/supervisor",   implemented: true, icon: "ShieldCheck", keywords: ["supervisor"], hidden: true },
        ],
    },
    {
        id: "press",
        label: "PRESS",
        icon: "FileText",
        allowFor: ["user", "admin", "super_admin"],
        items: [
            { id: "press-releases",     label: "Releases",     to: "/app/press/releases",     implemented: true, icon: "FileText", keywords: ["press", "release"] },
            { id: "press-generator",    label: "Generator",    to: "/app/press/generator",    implemented: true, icon: "Sparkles", keywords: ["generator"] },
            { id: "press-distribution", label: "Distribution", to: "/app/press/distribution", implemented: true, icon: "Mail",     keywords: ["distribuzione"] },
        ],
    },
    {
        id: "distribuzione",
        label: "Distribuzione",
        icon: "Layers",
        allowFor: ["admin", "super_admin"],
        hidden: true, /* Phase VI.5 Wave A · HIDE cluster · future_phase_hint=phase_vii_distribution_ops */
        items: [
            { id: "dist-jobs",     label: "Lavori di distribuzione",  to: "/app/distribution/jobs",     implemented: true, icon: "Workflow", keywords: ["jobs"], hidden: true },
            { id: "dist-channels", label: "Canali di distribuzione",  to: "/app/distribution/channels", implemented: true, icon: "Layers",   keywords: ["canali"], hidden: true },
            { id: "dist-email",    label: "Email",                    to: "/app/distribution/email",    implemented: true, icon: "Mail",     keywords: ["email"], hidden: true },
            { id: "dist-webhook",  label: "Webhook",                  to: "/app/distribution/webhook",  implemented: true, icon: "Webhook",  keywords: ["webhook"], hidden: true },
            { id: "dist-rss",      label: "RSS",                      to: "/app/distribution/rss",      implemented: true, icon: "Mail",     keywords: ["rss"], hidden: true },
            { id: "dist-atom",     label: "Atom",                     to: "/app/distribution/atom",     implemented: true, icon: "Mail",     keywords: ["atom"], hidden: true },
        ],
    },
    {
        id: "autorevolezza",
        label: "Autorevolezza",
        icon: "ShieldCheck",
        allowFor: ["admin", "super_admin"],
        hidden: true, /* Phase VI.5 Wave A · HIDE cluster · future_phase_hint=phase_vii_authority */
        items: [
            { id: "au-policies",   label: "Policy",       to: "/app/authority/policies",   implemented: true, icon: "ScrollText",  keywords: ["policy"], hidden: true },
            { id: "au-supervisor", label: "Supervisore",  to: "/app/authority/supervisor", implemented: true, icon: "ShieldCheck", keywords: ["supervisor"], hidden: true },
            { id: "au-decisions",  label: "Decisioni",    to: "/app/authority/decisions",  implemented: true, icon: "Sparkles",    keywords: ["decisioni"], hidden: true },
            { id: "au-approvals",  label: "Approvazioni", to: "/app/authority/approvals",  implemented: true, icon: "ShieldCheck", keywords: ["approvazioni"], hidden: true },
            { id: "au-workflows",  label: "Workflow",     to: "/app/authority/workflows",  implemented: true, icon: "Workflow",    keywords: ["workflow"], hidden: true },
        ],
    },
    // Wave Marketplace V10 · MP.6 Sidebar Restructure · 3 sezioni dedicate (21 link totali).
    // Sostituisce il legacy gruppo "marketplace-creator" (6 link ridondanti).
    {
        id: "marketplace",
        label: "Marketplace",
        icon: "Store",
        allowFor: ["user", "admin", "super_admin"],
        items: [
            { id: "mp10-catalog",     label: "Esplora catalogo",   to: "/marketplace/v10",                 implemented: true, icon: "Store",       keywords: ["catalogo", "esplora", "marketplace", "v10"] },
            // MP.7 Hotfix R3 · "Marketplace Hub" ripristinato · punta ora alla
            // versione SPA /marketplace/hub (sidebar persistente V12).
            // La landing SSR Next.js a /marketplace resta invariata per SEO.
            { id: "mp10-hub",         label: "Marketplace Hub",    to: "/marketplace/hub",                 implemented: true, icon: "ShoppingBag", keywords: ["hub", "marketplace"] },
            { id: "mp10-wishlist",    label: "Wishlist",           to: "/marketplace/wishlist",            implemented: true, icon: "Bookmark",    keywords: ["wishlist", "preferiti"] },
            { id: "mp10-cart",        label: "Carrello",           to: "/cart",                            implemented: true, icon: "ShoppingBag", keywords: ["carrello", "cart"] },
            { id: "mp10-library",     label: "La mia libreria",    to: "/marketplace/library",             implemented: true, icon: "BookOpen",    keywords: ["libreria", "library"] },
            { id: "mp10-bookings",    label: "Service bookings",   to: "/marketplace/service-bookings",    implemented: true, icon: "FileText",    keywords: ["booking", "service"] },
            { id: "mp10-redemptions", label: "Token redemptions",  to: "/marketplace/token-redemptions",   implemented: true, icon: "Coins",       keywords: ["token", "redemption"] },
            // Phase IX.F.2 · Suites v10 (9 preconfigured bundles)
            { id: "suites-v10",       label: "Suites",             to: "/app/suites",                      implemented: true, icon: "Boxes",       keywords: ["suite", "bundle", "pacchetto"] },
            { id: "suites-v10-me",    label: "Le mie suites",      to: "/app/suites/me",                   implemented: true, icon: "Layers",      keywords: ["suite", "owned", "library"] },
        ],
    },
    // ================================================================
    // Phase IX · Ranking · Reputation · Automations · Universe hubs
    // ================================================================
    {
        id: "ranking-reputation",
        label: "Ranking & Reputation",
        icon: "Trophy",
        allowFor: ["user", "admin", "super_admin"],
        items: [
            { id: "ix-e-ranking-hub",    label: "Ranking hub",          to: "/app/ranking",   implemented: true, icon: "Trophy",      keywords: ["ranking", "leaderboard", "badge"] },
            { id: "ix-f1-leaderboard",   label: "Public leaderboard",   to: "/leaderboard",   implemented: true, icon: "LineChart",   keywords: ["leaderboard", "public", "seo"] },
            { id: "ix-a-reputation",     label: "La mia reputation",    to: "/app/reputation", implemented: true, icon: "ShieldCheck", keywords: ["reputation", "score", "tier"] },
        ],
    },
    {
        id: "automations",
        label: "Automations",
        icon: "Zap",
        allowFor: ["user", "admin", "super_admin"],
        items: [
            { id: "ix-d-automations-hub",   label: "Automations Hub",      to: "/app/automations",            implemented: true, icon: "Zap",      keywords: ["automation", "workflow", "trigger"] },
            // admin-only entries
            { id: "ix-d-workflow-builder",  label: "Workflow builder",     to: "/app/automations/workflows",  implemented: true, icon: "Sparkles", keywords: ["workflow", "builder"], requiredRoles: ["admin", "super_admin"] },
            { id: "ix-f3-trigger-log",      label: "Trigger log",          to: "/app/automations#trigger-log", implemented: true, icon: "Activity", keywords: ["trigger", "log"], requiredRoles: ["admin", "super_admin"] },
        ],
    },
    {
        id: "universe-ecosystem",
        label: "Universe & Ecosystem",
        icon: "Globe",
        allowFor: ["user", "admin", "super_admin"],
        items: [
            { id: "ix-c-universe-hub",   label: "Universe Hub",     to: "/app/universe",   implemented: true, icon: "Sparkles", keywords: ["universe", "simulation", "instance"] },
            { id: "ix-c-ecosystem-hub",  label: "Ecosystem Hub",    to: "/app/ecosystem",  implemented: true, icon: "Globe",    keywords: ["ecosystem", "activation"] },
            { id: "ix-b-civ-ops",        label: "Civilization Ops", to: "/admin/ai-civ-ops", implemented: true, icon: "Workflow", keywords: ["civilization", "ops", "squad", "deployment"], requiredRoles: ["admin", "super_admin"] },
        ],
    },
    {
        id: "creator-studio",
        label: "Creator Studio",
        icon: "Sparkles",
        allowFor: ["user", "admin", "super_admin"],
        // MP.6 · conditional su creator profile · fetched runtime da V12UserLayout
        items: [
            { id: "cs-apply",            label: "Diventa Creator",   to: "/marketplace/creator/apply",             implemented: true, icon: "User",      keywords: ["creator", "diventa", "apply"], nonCreatorOnly: true },
            { id: "cs-dashboard",        label: "Dashboard",         to: "/marketplace/creator",                   implemented: true, icon: "Gauge",     keywords: ["dashboard", "creator"], creatorOnly: true, badge: "creator_notifications_unread" },
            { id: "cs-assets",           label: "I miei asset",      to: "/marketplace/creator/assets",            implemented: true, icon: "Boxes",     keywords: ["asset", "my"], creatorOnly: true },
            { id: "cs-new-asset",        label: "Nuovo asset",       to: "/marketplace/creator/assets/new",        implemented: true, icon: "Sparkles",  keywords: ["nuovo", "asset", "create"], creatorOnly: true },
            { id: "cs-service-bookings", label: "Service bookings",  to: "/marketplace/creator/service-bookings",  implemented: true, icon: "FileText",  keywords: ["booking", "service"], creatorOnly: true },
            { id: "cs-earnings",         label: "Guadagni e payout", to: "/marketplace/creator/earnings",          implemented: true, icon: "Coins",     keywords: ["guadagni", "payout", "earnings"], creatorOnly: true },
        ],
    },
    {
        id: "admin-marketplace",
        label: "Admin Marketplace",
        icon: "ShieldCheck",
        allowFor: ["admin", "super_admin"],
        items: [
            { id: "adm-mp-overview",   label: "Overview",            to: "/admin/marketplace/v10",         implemented: true, icon: "LayoutDashboard", keywords: ["overview", "admin", "marketplace"] },
            { id: "adm-mp-moderation", label: "Moderation queue",    to: "/admin/marketplace/moderation",  implemented: true, icon: "ShieldCheck",     keywords: ["moderazione", "queue"], badge: "admin_moderation_pending" },
            { id: "adm-mp-assets",     label: "Gestione asset",      to: "/admin/marketplace/assets",      implemented: true, icon: "Boxes",           keywords: ["asset", "gestione"] },
            { id: "adm-mp-creators",   label: "Gestione creator",    to: "/admin/marketplace/creators",    implemented: true, icon: "Users",           keywords: ["creator", "gestione"] },
            { id: "adm-mp-reviews",    label: "Reviews moderation",  to: "/admin/marketplace/reviews",     implemented: true, icon: "FileText",        keywords: ["review", "moderazione"] },
            { id: "adm-mp-payouts",    label: "Payouts",             to: "/admin/marketplace/payouts",     implemented: true, icon: "Coins",           keywords: ["payout", "pagamenti"] },
            { id: "adm-mp-fee-config", label: "Fee config",          to: "/admin/marketplace/fee-config",  implemented: true, icon: "Settings",        keywords: ["fee", "commission"] },
            { id: "adm-mp-audit",      label: "Audit log",           to: "/admin/marketplace/audit",       implemented: true, icon: "ScrollText",      keywords: ["audit", "log"] },
        ],
    },
    {
        id: "content",
        label: "Content",
        icon: "BookOpen",
        allowFor: ["user", "admin", "super_admin"],
        items: [
            { id: "ct-blog",     label: "Blog (classico)",       to: "/app/content/blog",     implemented: true, icon: "BookOpen", keywords: ["blog"] },
            // MP.7 Hotfix R2 · "Articoli blog (user)" rimosso dalla sidebar (duplicato
            // di Blog classico) · pagina resta accessibile via URL /app/content/articles.
        ],
    },
    // Wave VII.A · Stripe Credit Packs · nuovo gruppo Billing.
    // R3 · aggiunto link "Pricing" come primo item (landing commerciale).
    // R5 · aggiunto "La mia subscription" dopo Pricing.
    {
        id: "billing",
        label: "Billing",
        icon: "Coins",
        allowFor: ["user", "admin", "super_admin"],
        items: [
            { id: "bl-pricing",      label: "Pricing",              to: "/pricing",            implemented: true, icon: "Tag",        keywords: ["pricing", "prezzi", "listino", "piani", "subscription"] },
            { id: "bl-mysub",        label: "La mia subscription", to: "/app/subscription",   implemented: true, icon: "ShieldCheck", keywords: ["subscription", "piano", "abbonamento", "fatture"] },
            { id: "bl-packs",        label: "Acquista crediti",     to: "/credits/packs",      implemented: true, icon: "Coins",      keywords: ["crediti", "pack", "stripe", "billing"] },
            { id: "bl-history",      label: "Storico transazioni", to: "/credits/history",    implemented: true, icon: "ScrollText", keywords: ["storico", "transazioni", "credits"] },
        ],
    },
    {
        id: "partnership",
        label: "Partnership",
        icon: "Users",
        allowFor: ["user", "admin", "super_admin", "affiliate_only"],
        items: [
            // MP.7 Hotfix R2 · ridotto da 5 a 3 link · "Affiliato" (legacy v1) e
            // "Materiali promo" rimossi dalla sidebar · pagine restano accessibili via URL.
            { id: "pt-affiliate-v2",      label: "Affiliato v2",          to: "/app/partnership/affiliate-v2", implemented: true, icon: "Users",     keywords: ["affiliato", "v2"] },
            { id: "pt-admin-affiliates",  label: "Dashboard affiliati",   to: "/app/partnership/dashboard",    implemented: true, icon: "LineChart", keywords: ["dashboard", "affiliati"] },
            // Phase IX.F.4 · A4 · Materiali promo ri-linkati (PRD V10 gap closure, MP.7 Hotfix R2 reversal)
            { id: "pt-materials",         label: "Materiali promo",       to: "/app/partnership/materials",    implemented: true, icon: "FileText",  keywords: ["materiali", "promo", "banner", "template"] },
            { id: "pt-payout",            label: "Payout",                to: "/app/partnership/payout",       implemented: true, icon: "Coins",     keywords: ["payout"] },
        ],
    },
    // MP.7 Hotfix R6 · Cluster "account" rimosso completamente dalla
    // sidebar · tutti gli 8 shortcut account (Profilo · Crediti · Storico
    // · Fatturazione · Prezzi · Email · Privacy · API Keys) sono stati
    // migrati nel dropdown avatar del topbar V12 (V12UserAvatarMenu.jsx).
    // Direct Reach Policy preservata · le route `/app/account/*` restano
    // registrate in App.js e deep-linkabili. Pattern UX standard SaaS:
    // utenti si aspettano i link account sotto l'avatar in top-right.
    //
    // Nota: `filterNavByRole` per `affiliate_only` continua a filtrare
    // su whitelist che include "account" · essendo il cluster rimosso,
    // il filtro ignora l'id orfano senza side-effect. Il dropdown avatar
    // è visibile a TUTTI i ruoli (incluso affiliate_only) via topbar.
    {
        id: "academy",
        label: "Academy",
        icon: "GraduationCap",
        allowFor: ["user", "admin", "super_admin"],
        items: [
            { id: "ac-courses",      label: "Corsi",       to: "/app/academy/courses",      implemented: true, icon: "BookOpen",    keywords: ["corsi"] },
            { id: "ac-progress",     label: "Progressi",   to: "/app/academy/progress",     implemented: true, icon: "LineChart",   keywords: ["progressi"] },
            { id: "ac-certificates", label: "Certificati", to: "/app/academy/certificates", implemented: true, icon: "ShieldCheck", keywords: ["certificati"] },
        ],
    },
    {
        id: "community-user",
        label: "Community",
        icon: "Users",
        allowFor: ["user", "admin", "super_admin"],
        items: [
            { id: "cm-feed",     label: "Feed",         to: "/app/community/feed",     implemented: true, icon: "Activity", keywords: ["feed", "community"] },
            { id: "cm-groups",   label: "Gruppi",       to: "/app/community/groups",   implemented: true, icon: "Users",    keywords: ["gruppi"] },
            { id: "cm-events",   label: "Eventi",       to: "/app/community/events",   implemented: true, icon: "Flag",     keywords: ["eventi"] },
            { id: "cm-messages", label: "Messaggi",     to: "/app/community/messages", implemented: true, icon: "Mail",     keywords: ["messaggi", "dm"] },
            { id: "cm-ai",       label: "AI Community", to: "/app/community/ai",       implemented: true, icon: "Sparkles", keywords: ["ai", "community"] },
        ],
    },
    {
        id: "integrazioni-seo",
        label: "SEO & Integrazioni",
        icon: "Search",
        allowFor: ["admin", "super_admin"],
        items: [
            { id: "seo-twitter",  label: "Twitter / X",            to: "/app/ai/social/twitter",  implemented: true, icon: "Hash",   keywords: ["twitter", "x", "social"], hidden: true /* Phase VI.5 Wave CL · HIDE · V12TwitterIntegrationShell placeholder */ },
            { id: "seo-linkedin", label: "LinkedIn",               to: "/app/ai/social/linkedin", implemented: true, icon: "Hash",   keywords: ["linkedin", "social"], hidden: true /* Phase VI.5 Wave CL · HIDE · V12LinkedInIntegrationShell placeholder */ },
            { id: "seo-gsc",      label: "Google Search Console",  to: "/app/seo/gsc",            implemented: true, icon: "Search", keywords: ["gsc", "google", "search"], hidden: true /* Phase VI.5 Wave CL · HIDE · V12GSCConnectPage placeholder */ },
            { id: "seo-bing",     label: "Bing Webmaster",         to: "/app/seo/bing",           implemented: true, icon: "Search", keywords: ["bing", "webmaster"], hidden: true /* Phase VI.5 Wave CL · HIDE · V12BingDashboardPage placeholder */ },
        ],
    },
    // Cluster 16 · Admin Area · visibile solo per role admin/super_admin.
    // F85 · admin pannelli NON sono stati migrati sotto /v2/* perché
    // vivono già sotto `/admin/*` con la loro chrome.
    // F88 · P1 hotfix · entrambe le voci ora usano SPA Link (`to:`).
    // Il bug F85 (auth-flush full-reload) riappariva su queste 2
    // voci perché erano le uniche rimaste con `legacyHref:`.
    // Le route `/admin/v2/dashboard` + `/admin` esistono già e
    // sono SPA-ready sotto React Router.
    {
        id: "admin-area",
        label: "Admin area",
        icon: "ShieldCheck",
        allowFor: ["admin", "super_admin"],
        items: [
            {
                id: "aa-v12",
                label: "Pannello Admin V12",
                to: "/admin/dashboard",
                implemented: true,
                icon: "ShieldCheck",
                keywords: ["admin", "v12", "pannello"],
            },
            {
                id: "aa-classic",
                label: "Pannello Admin (classico)",
                to: "/admin",
                implemented: true,
                icon: "ShieldCheck",
                keywords: ["admin", "classico", "legacy"],
            },
            {
                // Wave VII.B · Admin Email Logs (Resend transactional)
                id: "aa-email",
                label: "Email logs",
                to: "/admin/email",
                implemented: true,
                icon: "Mail",
                keywords: ["email", "resend", "logs", "transactional"],
            },
            {
                // Wave VIII.A · GlobalAIEngine orchestrator admin
                id: "aa-ai-engine",
                label: "AI Engine",
                to: "/admin/ai-engine",
                implemented: true,
                icon: "Cpu",
                keywords: ["ai", "orchestrator", "engine", "tasks", "automation"],
            },
            {
                // Wave VIII.B · AutoCreationEngine admin (asset auto-generation)
                id: "aa-auto-creation",
                label: "Auto-Creation",
                to: "/admin/ai-engine/auto-creation",
                implemented: true,
                icon: "Sparkles",
                keywords: ["ai", "auto", "creation", "asset", "marketplace", "claude"],
            },
            {
                // Wave VIII.C · AutoExpansionEngine admin (module/suite proposals)
                id: "aa-auto-expansion",
                label: "Auto-Expansion",
                to: "/admin/ai-engine/auto-expansion",
                implemented: true,
                icon: "GitBranchPlus",
                keywords: ["ai", "expansion", "module", "suite", "proposal", "growth"],
            },
            {
                // Wave VIII.D · AutoMarketplaceEngine admin (pricing + promos + tokenomics)
                id: "aa-auto-marketplace",
                label: "Auto-Marketplace",
                to: "/admin/ai-engine/auto-marketplace",
                implemented: true,
                icon: "TrendingUp",
                keywords: ["ai", "marketplace", "pricing", "promotion", "tokenomics"],
            },
            {
                // Wave VIII.E · AutoSEOEngine admin (topic + brief + article + AB)
                id: "aa-auto-seo",
                label: "Auto-SEO",
                to: "/admin/ai-engine/auto-seo",
                implemented: true,
                icon: "Search",
                keywords: ["ai", "seo", "content", "blog", "topic", "ab-test"],
            },
            {
                // Wave VIII.F · AutoGovernanceEngine admin (scoring + moderation + override)
                id: "aa-auto-governance",
                label: "Auto-Governance",
                to: "/admin/ai-engine/auto-governance",
                implemented: true,
                icon: "ShieldCheck",
                keywords: ["ai", "governance", "moderation", "policy", "fraud", "score"],
            },
            {
                // Wave VIII.G · AutoRoutingEngine admin (lead/ticket/content/action routing)
                id: "aa-auto-routing",
                label: "Auto-Routing",
                to: "/admin/ai-engine/auto-routing",
                implemented: true,
                icon: "Target",
                keywords: ["ai", "routing", "lead", "ticket", "assignment", "sales"],
            },
            {
                // Wave VIII.H · AutoRecommendationEngine admin (personalized recs + upsell + coach)
                id: "aa-auto-recommendation",
                label: "Auto-Recommendation",
                to: "/admin/ai-engine/auto-recommendation",
                implemented: true,
                icon: "Sparkles",
                keywords: ["ai", "recommendation", "upsell", "coach", "ctr", "cvr"],
            },
            {
                // Wave VIII.I · Onboarding + Support combo admin
                id: "aa-onboarding-support",
                label: "Onboarding & Support",
                to: "/admin/ai-engine/onboarding-support",
                implemented: true,
                icon: "GraduationCap",
                keywords: ["ai", "onboarding", "support", "chatbot", "kb"],
            },
            {
                // Wave VIII.J · Visionary engines (Ecosystem · Universe · Civilization · Replication)
                id: "aa-visionary",
                label: "Visionary Engines",
                to: "/admin/ai-engine/visionary",
                implemented: true,
                icon: "Globe",
                keywords: ["ai", "ecosystem", "universe", "civilization", "replication", "simulation"],
            },
        ],
    },
];

// Flatten per command palette / mega-menu search · role-aware.
export function flattenUserNavItems(role) {
    const out = [];
    for (const g of filterNavByRole(V12_USER_NAV_GROUPS, role)) {
        for (const it of g.items) {
            out.push({ ...it, groupId: g.id, groupLabel: g.label });
        }
    }
    return out;
}

// Filtro cluster in base al ruolo utente.
// affiliate_only ha scope ristretto · forziamo subset specifico (whitelist).
// Supporto `requiredRoles` item-level (PRD V10 compliance): nasconde items admin-only
// anche dentro cluster user-visible (es. AI Suite > Civilization).
// Phase VI.5 Wave A · supporto `hidden: true` cluster-level e item-level per
// policy 2a (HIDE). Items/cluster con `hidden: true` non appaiono in sidebar;
// Route restano registrate in App.js (deep-link funziona).
// Drop cluster che restano senza items dopo filter.
export function filterNavByRole(groups, role) {
    const normalisedRole = _normaliseRole(role);
    if (normalisedRole === "affiliate_only") {
        // Scope ristretto · solo Workspace minimale + Partnership + Account.
        const allowed = new Set(["workspace", "partnership", "account"]);
        return groups
            .filter((g) => allowed.has(g.id) && !g.hidden)
            .map((g) => {
                if (g.id === "workspace") {
                    // Workspace ridotto · solo Dashboard + Notifiche.
                    return {
                        ...g,
                        items: g.items.filter((i) =>
                            !i.hidden &&
                            ["workspace-dashboard", "workspace-notifications"].includes(i.id),
                        ),
                    };
                }
                return { ...g, items: g.items.filter((i) => !i.hidden) };
            });
    }
    return groups
        .filter((g) => !g.hidden && (g.allowFor || []).includes(normalisedRole))
        .map((g) => ({
            ...g,
            items: g.items.filter(
                (it) =>
                    !it.hidden &&
                    (!it.requiredRoles || it.requiredRoles.includes(normalisedRole)),
            ),
        }))
        .filter((g) => g.items.length > 0);
}

function _normaliseRole(role) {
    if (!role) return "user";
    const r = String(role).toLowerCase();
    if (r === "super_admin" || r === "superadmin") return "super_admin";
    if (r === "admin") return "admin";
    if (r === "affiliate_only" || r === "affiliate") return "affiliate_only";
    if (r === "ai_agent") return "ai_agent";
    return "user";
}

export const V12_USER_NAV_TOTAL_ITEMS = V12_USER_NAV_GROUPS.reduce(
    (acc, g) => acc + g.items.length,
    0,
);

// Lista ruoli che devono fare redirect a `/dashboard` legacy (fail-safe).
export const V12_USER_FORBIDDEN_ROLES = ["ai_agent"];

export function isForbiddenRole(role) {
    const n = _normaliseRole(role);
    return V12_USER_FORBIDDEN_ROLES.includes(n);
}

// Ruoli ammessi su route placeholder admin-only V12 (SEO & Integrazioni, ecc).
export const V12_ADMIN_ONLY_ROLES = ["admin", "super_admin"];

export function isAdminRole(role) {
    const n = _normaliseRole(role);
    return V12_ADMIN_ONLY_ROLES.includes(n);
}

export { _normaliseRole as normaliseUserRole };


// ============================================================
// Hotfix FASE C · V12 Achromatic Strict · Sidebar MINIMAL (10 cluster + launcher)
// ------------------------------------------------------------
// Additive export: coesiste con V12_USER_NAV_GROUPS (preserva test).
// Base: Top 20 daily-usage dall'audit /app/memory/user_pages_audit.md.
// Ogni cluster ha 2-4 voci top-usage visibili.
// Il resto delle ~167 route è accessibile da `/app/launcher` (UserHubPage).
// Zero hidden stub phase VIII/IX.
// Hotfix Discovery · base `_V12_USER_NAV_MINIMAL_RAW` + extensions → export MINIMAL.
const _V12_USER_NAV_MINIMAL_RAW = [
    {
        id: "dashboard",
        label: "Dashboard",
        icon: "LayoutDashboard",
        allowFor: ["user", "admin", "super_admin", "affiliate_only"],
        items: [
            { id: "min-dashboard",        label: "Dashboard",            to: "/app/dashboard",     implemented: true, icon: "LayoutDashboard" },
            { id: "min-dashboard-legacy", label: "Dashboard (legacy)",   to: "/dashboard/legacy",  implemented: true, icon: "History" },
            { id: "min-launcher-group",   label: "Launcher",             to: "/app/launcher",      implemented: true, icon: "LayoutGrid" },
            { id: "min-notifications",    label: "Notifiche",            to: "/app/notifications", implemented: true, icon: "Bell" },
            { id: "min-stats",            label: "Statistiche",          to: "/app/stats",         implemented: true, icon: "LineChart" },
        ],
    },
    {
        id: "lead",
        label: "Lead",
        icon: "Target",
        allowFor: ["user", "admin", "super_admin"],
        items: [
            { id: "min-leads",         label: "Lead Engine",     to: "/app/leads",         implemented: true, icon: "Target" },
            { id: "min-leads-saved",   label: "Lead salvati",    to: "/app/leads/saved",   implemented: true, icon: "Bookmark" },
        ],
    },
    {
        id: "outreach",
        label: "Outreach",
        icon: "Send",
        allowFor: ["user", "admin", "super_admin"],
        items: [
            { id: "min-outreach-camp", label: "Campagne",        to: "/app/outreach/campaigns", implemented: true, icon: "Mail" },
            { id: "min-outreach-seq",  label: "Sequenze",        to: "/app/outreach/sequences", implemented: true, icon: "Layers" },
            { id: "min-outreach-tpl",  label: "Template",        to: "/app/outreach/templates", implemented: true, icon: "FileText" },
        ],
    },
    {
        id: "crm",
        label: "CRM",
        icon: "Users",
        allowFor: ["user", "admin", "super_admin"],
        items: [
            { id: "min-crm-contacts",  label: "Contatti",        to: "/app/crm/contatti",  implemented: true, icon: "User" },
            { id: "min-crm-pipeline",  label: "Pipeline",        to: "/app/crm/pipeline",  implemented: true, icon: "Workflow" },
            { id: "min-crm-aziende",   label: "Aziende",         to: "/app/crm/aziende",   implemented: true, icon: "Building2" },
        ],
    },
    {
        id: "content",
        label: "Content",
        icon: "PenTool",
        allowFor: ["user", "admin", "super_admin"],
        items: [
            { id: "min-content-articles", label: "Articoli",     to: "/app/content/articles", implemented: true, icon: "FileText" },
            { id: "min-content-blog",     label: "Blog",         to: "/app/content/blog",     implemented: true, icon: "BookOpen" },
            { id: "min-ai-longform",      label: "AI Long-form", to: "/app/ai/seo/long-form", implemented: true, icon: "PenTool" },
        ],
    },
    {
        id: "marketplace",
        label: "Marketplace",
        icon: "ShoppingBag",
        allowFor: ["user", "admin", "super_admin"],
        items: [
            { id: "min-mp-hub",       label: "Catalog",          to: "/app/marketplace/hub",     implemented: true, icon: "ShoppingBag" },
            { id: "min-mp-library",   label: "La mia libreria",  to: "/marketplace/library",     implemented: true, icon: "Package" },
            { id: "min-mp-wishlist",  label: "Wishlist",         to: "/marketplace/wishlist",    implemented: true, icon: "Heart" },
            { id: "min-mp-cart",      label: "Carrello",         to: "/cart",                    implemented: true, icon: "ShoppingCart" },
        ],
    },
    {
        id: "ai",
        label: "AI",
        icon: "Sparkles",
        allowFor: ["user", "admin", "super_admin"],
        items: [
            { id: "min-ai-agents",    label: "Agents",           to: "/app/ai/agents",     implemented: true, icon: "Bot" },
            { id: "min-ai-prompts",   label: "Prompts",          to: "/app/ai/prompts",    implemented: true, icon: "Lightbulb" },
            { id: "min-automations",  label: "Automations",     to: "/app/automations",   implemented: true, icon: "Zap" },
        ],
    },
    {
        id: "billing",
        label: "Credits & Billing",
        icon: "CreditCard",
        allowFor: ["user", "admin", "super_admin", "affiliate_only"],
        items: [
            { id: "min-credits",       label: "Credits",         to: "/app/credits",         implemented: true, icon: "Coins" },
            { id: "min-subscription",  label: "Subscription",    to: "/app/subscription",    implemented: true, icon: "Receipt" },
            { id: "min-pricing",       label: "Pricing",         to: "/app/pricing",         implemented: true, icon: "DollarSign" },
        ],
    },
    {
        id: "analytics",
        label: "Analytics",
        icon: "BarChart3",
        allowFor: ["user", "admin", "super_admin"],
        items: [
            { id: "min-suites",       label: "Suites",           to: "/app/suites",         implemented: true, icon: "Briefcase" },
            { id: "min-reputation",   label: "Reputation",       to: "/app/reputation",     implemented: true, icon: "Shield" },
        ],
    },
    {
        id: "account",
        label: "Account",
        icon: "User",
        allowFor: ["user", "admin", "super_admin", "affiliate_only"],
        items: [
            { id: "min-profile",      label: "Profilo",          to: "/app/account/profilo",      implemented: true, icon: "User" },
            { id: "min-privacy",      label: "Privacy",          to: "/app/account/privacy",      implemented: true, icon: "Lock" },
            { id: "min-apikeys",      label: "API Keys",         to: "/app/account/api-keys",     implemented: true, icon: "KeyRound" },
            { id: "min-partnership",  label: "Partnership",      to: "/app/partnership/dashboard", implemented: true, icon: "Handshake" },
        ],
    },
    {
        // Footer flat link · esplode in UserHubPage.
        id: "launcher",
        label: "All modules",
        icon: "Boxes",
        allowFor: ["user", "admin", "super_admin", "affiliate_only"],
        flat: true,
        items: [
            { id: "min-launcher", label: "→ All modules", to: "/app/launcher", implemented: true, icon: "Boxes", hero: true },
        ],
    },
];

// Hotfix Discovery · applica extensions (nuovo cluster `discovery` dopo `lead` +
// item `ai-rag` appended al cluster `ai`). Pattern coerente con admin
// `applyLegacyPatches`. Il raw base resta esportato per test di regressione.
// Hotfix P1 Community · chain · applyUserCommunityExtensions aggiunge cluster
// `community` dopo `discovery` · idempotente · zero-touch sul raw.
// Hotfix admin-shortcuts · chain · applyUserAdminShortcutsExtensions aggiunge
// cluster `admin-shortcuts` dopo `community` · allowFor:[admin,super_admin] ·
// invisibile a user standard tramite filterNavByRole runtime.
export const V12_USER_NAV_MINIMAL_BASE = _V12_USER_NAV_MINIMAL_RAW;
export const V12_USER_NAV_MINIMAL = applyUserAdminShortcutsExtensions(
    applyUserCommunityExtensions(
        applyUserScaleFeaturesExtensions(
            applyUserDiscoveryExtensions(_V12_USER_NAV_MINIMAL_RAW),
        ),
    ),
);

export const V12_USER_NAV_MINIMAL_TOTAL = V12_USER_NAV_MINIMAL.reduce(
    (acc, g) => acc + g.items.length,
    0,
);
